// Button de shadcn, copiado al repo y adaptado a la identidad de UCORP
// (gestion/07_identidad/components/core/Boton.prompt.md) sobre base slate:
// acero claro como interaccion (sobre oscuro el 600 no llega al contraste),
// radio 6px, peso medium, y una sola accion principal por vista.
// Las variantes son las de identidad (primario, secundario, fantasma) mas el
// enlace y el peligro de las bajas; las de shadcn que no existen en el
// sistema no se copian.
//
// `asChild` es el Slot de Radix: deja renderizar un <a> con el look de boton
// sin anidar un boton dentro de un link, que es el error de accesibilidad que
// el patron evita.
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn.js";

const estilos = cva(
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
    "font-sans font-medium leading-apretado",
    "transition-colors duration-150 ease-estandar",
    "cursor-pointer active:scale-[0.99] disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primario: "bg-acero text-blanco hover:bg-acero-oscuro",
        secundario: "border border-linea-fuerte bg-transparent text-texto-2 hover:bg-velo hover:text-blanco",
        fantasma: "border border-transparent bg-transparent text-texto-suave hover:bg-velo hover:text-texto-2",
        peligro: "border border-critico/40 bg-transparent text-critico-texto hover:bg-critico/10",
        enlace: "text-acero-texto underline underline-offset-2 hover:text-texto",
      },
      size: {
        sm: "h-[26px] rounded-sm px-2.5 text-xs",
        md: "h-8 rounded-md px-3 text-sm",
        lg: "h-10 rounded-md px-4 text-base",
        /** Solo texto: sin caja, para acciones terciarias en linea. */
        inline: "h-auto p-0 text-sm",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primario", size: "md", block: false },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof estilos> {
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  type = "button",
  ...props
}: ButtonProps): React.JSX.Element {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(estilos({ variant, size, block }), className)}
      {...(asChild ? {} : { type })}
      {...props}
    />
  );
}
